import dayjs from "dayjs";
import type {
  ApiContractPackageDto,
  ApiPersonServiceDto,
  ContractRow,
  ContractTemplateBlockDto,
  PersonServiceRow
} from "./personDetailTypes";
import { mapApiPackageToContractRow } from "./personDetailUtils";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { ACCESS_SCOPE_ANY_STUDIO } from "../../../app/constants/accessScope";

const SERVICE_ORDER_PLACEHOLDER = 2;

type BuildPersonContractRowsArgs = {
  contractPackages: ApiContractPackageDto[];
  services: ApiPersonServiceDto[];
  currentBlocks: ContractTemplateBlockDto[];
  fallbackBlocks: ContractTemplateBlockDto[];
  showHistoricalContracts: boolean;
};

function createServiceProductTypeMap(blocks: ContractTemplateBlockDto[]): Map<string, number> {
  const typeByServiceProductId = new Map<string, number>();
  for (const block of blocks) {
    const id = block.id ? String(block.id) : "";
    if (!id) continue;
    typeByServiceProductId.set(id, Number(block.type || 0));
  }
  return typeByServiceProductId;
}

function mapApiServiceToRow(
  service: ApiPersonServiceDto,
  contractPackageId: string,
  typeByServiceProductId: Map<string, number>
): PersonServiceRow {
  return {
    id: String(service.id),
    sourceContractPackageId: contractPackageId,
    name: String(service.name || ""),
    contractTemplateId: service.contractTemplateId ? String(service.contractTemplateId) : null,
    serviceProductType: service.contractTemplateId ? (typeByServiceProductId.get(String(service.contractTemplateId)) ?? null) : null,
    isPrimary: service.isPrimary === true,
    startDate: String(service.startDate || ""),
    endDate: service.endDate ?? null,
    validTo: service.validTo ?? null,
    value: service.value ?? null,
    usageLimitValue: service.usageLimitValue ?? null,
    usageLimitUnit: service.usageLimitUnit ?? null,
    paymentCycleValue: service.paymentCycleValue ?? null,
    paymentCycleUnit: service.paymentCycleUnit ?? null,
    durationValue: service.durationValue ?? null,
    durationUnit: service.durationUnit ?? null,
    renewalValue: service.renewalValue ?? null,
    renewalUnit: service.renewalUnit ?? null,
    homeStudioId: service.homeStudioId ?? null,
    accessScope: Number(service.accessScope || ACCESS_SCOPE_ANY_STUDIO),
    allowedStudioIds: (service.allowedStudioIds || []).map(String),
    requiresNoArrears: service.requiresNoArrears !== false,
    allowsAccess: service.allowsAccess !== false,
    isActive: service.isActive !== false,
    isLocked: service.isLocked === true,
    isEnded: service.isEnded === true,
    isManualOverride: service.isManualOverride === true,
    note: service.note ?? null,
    createdAt: String(service.createdAt || ""),
    paymentMethod: service.paymentMethod != null ? Number(service.paymentMethod) : null,
    sepaGroupId: service.sepaGroupId ? String(service.sepaGroupId) : null
  };
}

function sortServices(
  services: PersonServiceRow[],
  blocks: ContractTemplateBlockDto[]
): PersonServiceRow[] {
  return [...services].sort((a, b) => {
    const aTemplateId = a.contractTemplateId ? String(a.contractTemplateId) : null;
    const bTemplateId = b.contractTemplateId ? String(b.contractTemplateId) : null;
    const aTemplate = aTemplateId ? blocks.find((template) => String(template.id) === aTemplateId) : null;
    const bTemplate = bTemplateId ? blocks.find((template) => String(template.id) === bTemplateId) : null;
    const aIsProportionalPlaceholder = aTemplate?.isProportionalCalculationPlaceholder === true;
    const bIsProportionalPlaceholder = bTemplate?.isProportionalCalculationPlaceholder === true;
    const orderA = a.isPrimary === true ? 0 : aIsProportionalPlaceholder ? SERVICE_ORDER_PLACEHOLDER : 1;
    const orderB = b.isPrimary === true ? 0 : bIsProportionalPlaceholder ? SERVICE_ORDER_PLACEHOLDER : 1;
    if (orderA !== orderB) return orderA - orderB;
    const aStart = a.startDate ? new Date(String(a.startDate)).getTime() : 0;
    const bStart = b.startDate ? new Date(String(b.startDate)).getTime() : 0;
    return aStart - bStart;
  });
}

function attachServicesToPackages(
  contractRows: ContractRow[],
  servicesByContractPackageId: Map<string, PersonServiceRow[]>,
  blocks: ContractTemplateBlockDto[]
): void {
  contractRows.forEach((contract) => {
    const sortedServices = sortServices(servicesByContractPackageId.get(contract.id) || [], blocks);

    contract.subServices = sortedServices;
    if (sortedServices.length > 1) {
      contract.children = sortedServices;
      contract.singleBlockChild = undefined;
    } else if (sortedServices.length === 1) {
      contract.children = undefined;
      contract.singleBlockChild = sortedServices[0];
    } else {
      contract.children = undefined;
      contract.singleBlockChild = undefined;
    }

    const primaryService = sortedServices.find((service) => service.isPrimary === true);
    if (primaryService && primaryService.paymentMethod != null) {
      contract.paymentMethod = primaryService.paymentMethod;
    }
  });
}

export function buildPersonContractRows({
  contractPackages,
  services,
  currentBlocks,
  fallbackBlocks,
  showHistoricalContracts
}: BuildPersonContractRowsArgs): ContractRow[] {
  const blocksToUse = currentBlocks.length > 0 ? currentBlocks : fallbackBlocks;
  const typeByServiceProductId = createServiceProductTypeMap(blocksToUse);
  const today = dayjs().startOf("day");
  let contractRows = contractPackages.map(mapApiPackageToContractRow);

  if (!showHistoricalContracts) {
    contractRows = contractRows.filter((row) => {
      const endIso = String(row.endDate || "").slice(0, ISO_DATE_LENGTH);
      const end = endIso ? dayjs(endIso) : null;
      return !end || !end.isValid() || !end.isBefore(today, "day");
    });
  }

  const servicesByContractPackageId = new Map<string, PersonServiceRow[]>();
  services.forEach((service) => {
    if (!service.sourceContractPackageId) return;
    const contractPackageId = String(service.sourceContractPackageId);
    if (!servicesByContractPackageId.has(contractPackageId)) {
      servicesByContractPackageId.set(contractPackageId, []);
    }
    servicesByContractPackageId
      .get(contractPackageId)!
      .push(mapApiServiceToRow(service, contractPackageId, typeByServiceProductId));
  });

  attachServicesToPackages(contractRows, servicesByContractPackageId, blocksToUse);
  return contractRows;
}
